import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';
import { Checkbox } from './Checkbox';

// Define options for the form
const corporateEventTypeOptions = [
    { value: 'conference-summit', label: 'Conference / Summit' },
    { value: 'gala-dinner-awards', label: 'Gala Dinner / Awards Night' },
    { value: 'product-launch', label: 'Product Launch' },
    { value: 'brand-activation', label: 'Brand Activation / Expo' },
    { value: 'team-building-retreat', label: 'Team Building / Corporate Retreat' },
    { value: 'other', label: 'Other' },
];

const guestCountOptions = [
    { value: '1-50', label: '1-50 Guests' },
    { value: '51-150', label: '51-150 Guests' },
    { value: '151-300', label: '151-300 Guests' },
    { value: '300-500', label: '300-500 Guests' },
    { value: '500+', label: '500+ Guests' },
];

const servicesOfInterestOptions = [
    { id: 'full-planning', label: 'Full Event Decor' },
    { id: 'venue-sourcing', label: 'Venue Sourcing & Management' },
    { id: 'technical-production', label: 'A/V & Technical Production' },
    { id: 'delegate-management', label: 'Equipment Provision' },
    { id: 'event-branding', label: 'Event Branding & Design' },
    { id: 'entertainment', label: 'Entertainment Sourcing' },
];

const CorporateQuoteModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        company: '',
        email: '',
        phone: '',
        eventType: '',
        guestCount: '',
        eventDate: '',
        services: [],
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleServiceToggle = (serviceId) => {
        setFormData(prev => {
            const newServices = prev.services.includes(serviceId)
                ? prev.services.filter(s => s !== serviceId)
                : [...prev.services, serviceId];
            return { ...prev, services: newServices };
        });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Contact name is required';
        if (!formData.company.trim()) newErrors.company = 'Company name is required';
        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
        if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'A valid email is required';
        if (!formData.eventType) newErrors.eventType = 'Please select an event type';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);

        const whatsappNumber = "+254727937010";
        const eventTypeLabel = corporateEventTypeOptions.find(opt => opt.value === formData.eventType)?.label;
        const guestCountLabel = guestCountOptions.find(opt => opt.value === formData.guestCount)?.label;
        const selectedServices = formData.services.map(sId => servicesOfInterestOptions.find(s => s.id === sId)?.label).join(', ');

        let message = `*👔 Corporate Event Proposal Request 👔*\n\n`;
        message += `*Contact Person:* ${formData.name}\n`;
        message += `*Company:* ${formData.company}\n`;
        message += `*Email:* ${formData.email}\n`;
        message += `*Phone:* ${formData.phone}\n\n`;
        message += `*Event Details:*\n`;
        message += `  - *Type of Event:* ${eventTypeLabel}\n`;
        if(formData.guestCount) message += `  - *Estimated Guests:* ${guestCountLabel}\n`;
        if(formData.eventDate) message += `  - *Envisioned Date:* ${formData.eventDate}\n`;
        if(selectedServices) message += `\n*Services of Interest:*\n${selectedServices}\n`;
        if (formData.message) message += `\n*Additional Information:*\n${formData.message}`;

        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
        
        setIsSubmitting(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
                    <div className="flex items-start justify-between">
                        <div>
                            <h2 className="font-serif font-bold text-2xl text-gray-900">Request a Proposal</h2>
                            <p className="font-sans text-gray-500 mt-1">Provide us with your event details to get started.</p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={onClose} className="-mt-2 -mr-2">
                            <Icon name="X" size={24} />
                        </Button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Contact Name" required value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} error={errors.name} />
                        <Input label="Company Name" required value={formData.company} onChange={(e) => handleInputChange('company', e.target.value)} error={errors.company} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Email Address" type="email" required value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} error={errors.email} />
                        <Input label="Phone Number" type="tel" required value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} error={errors.phone} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select label="Type of Event" required options={corporateEventTypeOptions} value={formData.eventType} onChange={(value) => handleInputChange('eventType', value)} error={errors.eventType} />
                        <Select label="Estimated Guests" options={guestCountOptions} value={formData.guestCount} onChange={(value) => handleInputChange('guestCount', value)} />
                    </div>
                    <div>
                        <Input label="Envisioned Event Date (Optional)" type="date" value={formData.eventDate} onChange={(e) => handleInputChange('eventDate', e.target.value)} min={new Date().toISOString().split('T')[0]} />
                    </div>
                    <div>
                        <label className="font-sans font-medium text-gray-800 mb-3 block">Services of Interest</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {servicesOfInterestOptions.map((service) => (
                                <Checkbox key={service.id} label={service.label} checked={formData.services.includes(service.id)} onChange={() => handleServiceToggle(service.id)} />
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="font-sans font-medium text-gray-800 mb-2 block">Additional Details (Optional)</label>
                        <textarea value={formData.message} onChange={(e) => handleInputChange('message', e.target.value)} placeholder="Tell us more about your event, objectives, or any specific requirements..." rows={3} className="w-full p-3 border border-gray-300 rounded-lg font-sans bg-white focus:outline-none focus:ring-2 focus:ring-primary" />
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                        <Button type="submit" variant="primary" loading={isSubmitting} fullWidth size="lg">
                            {isSubmitting ? 'Submitting...' : 'Send Proposal Request'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CorporateQuoteModal;